import { usePostHog } from '@posthog/react';
import { useCreateDialogForm } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import React, { useEffect, useRef } from 'react';
import { Control, FieldValues, FormProvider, UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddContactMutation, useDeleteContactMutation, useUpdateContactMutation } from 'store/api/contacts';
import {
    ContactDataCreateOrUpdateCompanyDto,
    ContactDataCreateOrUpdatePersonDto,
    ContactForm,
    EinvoiceOperatorType,
    PartyBlockDto,
    PartyRelationshipTypeCode,
    PartyType,
    RelationshipTypeCode,
} from 'types/contacts';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { partyNameString } from 'utils/partyNameUtil';
import { useGoBack, useLeaveDeletedEntity, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { useRejectedSubmitCapture } from 'utils/useRejectedSubmitCapture';
import BlockingPolicyEditor from './blocking/BlockingPolicyEditor';
import { buildBlocksPayload } from './blocking/BlockingPolicyReadonlyShared';
import { contactFormToRequest } from './contactFormMapping';

import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import { useFuzzySearch } from 'hooks/useFuzzySearch';
import { CustomFieldDto } from 'types/customFields';

import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { AboutFields } from './form-sections/AboutFields';
import { BillingFields } from './form-sections/BillingFields';
import { ChooseTypeFields } from './form-sections/ChooseTypeFields';
import { CompanyFields } from './form-sections/CompanyFields';
import { ComplianceFields } from './form-sections/ComplianceFields';
import { ContactDetailsFields } from './form-sections/ContactDetailsFields';
import { IdentityDocumentFields } from './form-sections/IdentityDocumentFields';
import { MarketingFields } from './form-sections/MarketingFields';
import { OtherFields } from './form-sections/OtherFields';
import { PersonFields } from './form-sections/PersonFields';
import { ContactFormProps, useContactForm } from './form-sections/useContactForm';

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    // Choose Type
    partyType: 'contacts.choose-type',
    // About
    relationshipType: 'contacts.about.title',
    partners: 'contacts.about.title',
    managers: 'contacts.about.title',
    administrators: 'contacts.about.title',
    // Person Details
    firstName: 'contacts.person.title',
    localFirstName: 'contacts.person.title',
    lastName: 'contacts.person.title',
    localLastName: 'contacts.person.title',
    personTitle: 'contacts.person.title',
    personManualTitle: 'contacts.person.title',
    position: 'contacts.person.title',
    personalCode: 'contacts.person.title',
    linkedinUrl: 'contacts.person.title',
    homeAddress: 'contacts.person.title',
    homeLocalAddress: 'contacts.person.title',
    employedBy: 'contacts.person.title',
    segmentType: 'contacts.person.title',
    serviceLevelType: 'contacts.person.title',
    // Company Details
    officialName: 'contacts.company.title',
    name: 'contacts.company.title',
    officialLocalName: 'contacts.company.title',
    regCode: 'contacts.company.title',
    website: 'contacts.company.title',
    registrationAddress: 'contacts.company.title',
    registrationLocalAddress: 'contacts.company.title',
    // Identity Document
    identityDocumentNumber: 'contacts.identity-document.title',
    identityDocumentDateOfIssue: 'contacts.identity-document.title',
    identityDocumentDateOfExpiry: 'contacts.identity-document.title',
    identityDocumentIssuingCountryCode: 'contacts.identity-document.title',
    identityDocumentIssuingAuthority: 'contacts.identity-document.title',
    // Contact Details
    mainPhone: 'contacts.contact-details',
    mainEmail: 'contacts.contact-details',
    mainMobile: 'contacts.contact-details',
    correspondenceAddress: 'contacts.contact-details',
    employees: 'contacts.contact-details',
    primaryContact: 'contacts.contact-details',
    visitingAddress: 'contacts.contact-details',
    // Billing
    isInternalClient: 'contacts.billing.title',
    clientReference: 'contacts.billing.title',
    notVatLiable: 'contacts.billing.title',
    vatRegNum: 'contacts.billing.title',
    vatCountryCode: 'contacts.billing.title',
    preferredBankAccount: 'contacts.billing.title',
    invoicingPaymentDays: 'contacts.billing.title',
    billingNotes: 'contacts.billing.title',
    invoicingInfo: 'contacts.billing.title',
    billingAddress: 'contacts.billing.title',
    billingLocalAddress: 'contacts.billing.title',
    preferredLanguage: 'contacts.billing.title',
    einvoiceAddress: 'contacts.billing.title',
    einvoiceOperatorCode: 'contacts.billing.title',
    einvoiceOperatorType: 'contacts.billing.title',
    payerParty: 'contacts.billing.title',
    useJointInvoicing: 'contacts.billing.title',
    issueInvoicesWithCurrentDate: 'contacts.billing.title',
    primaryRecipientParty: 'contacts.billing.title',
    primaryRecipientAdditionalEmails: 'contacts.billing.title',
    secondaryRecipientParties: 'contacts.billing.title',
    secondaryRecipientAdditionalEmails: 'contacts.billing.title',
    invoicingDeadlineDay: 'contacts.billing.title',
    externalBillingInfo: 'contacts.billing.title',
    invoiceArchiveEmail: 'contacts.billing.title',
    invoiceTemplate: 'contacts.billing.title',
    invoiceSpecTemplate: 'contacts.billing.title',
    isSpecIncludedInInvoice: 'contacts.billing.title',
    invoiceCustomization: 'contacts.billing.title',
    paymentNotificationReceivers: 'contacts.billing.title',
    // Marketing
    preferredMailingLanguageTypeCode: 'contacts.marketing.title',
    mailingPreferences: 'contacts.marketing.title',
    marketingAreaTypes: 'contacts.marketing.title',
    countries: 'contacts.marketing.title',
    // Other
    isPoliticallyExposedPerson: 'contacts.other.title',
    dmsLink: 'contacts.other.title',
    notes: 'contacts.other.title',
    coiNotes: 'contacts.other.title',
    relatedKeywords: 'contacts.other.title',
    cooperationAreaTypes: 'contacts.other.title',
    // Compliance
    complianceVerificationStatusTypeCode: 'contacts.compliance.title',
    complianceVerificationNotes: 'contacts.compliance.title',
    riskProfileTypeCode: 'contacts.compliance.title',
    riskProfileNotes: 'contacts.compliance.title',
    riskFactors: 'contacts.compliance.title',
    isServiceAgreementNotNeeded: 'contacts.compliance.title',
    isUboNotNeeded: 'contacts.compliance.title',
    isPersonaNonGrata: 'contacts.compliance.title',
};

const PERSON_FIELD_TO_SECTION_MAP: Record<string, string> = {
    ...FIELD_TO_SECTION_MAP,
    name: 'contacts.person.title',
    relatedKeywords: 'contacts.person.title',
    areaOfResponsibilityTypes: 'contacts.person.title',
};

/**
 * Isolates name-field watching and related effects from the main form component
 * to prevent re-rendering the entire form tree on every keystroke in name fields.
 */
const NameFieldSyncer = ({
    control,
    setValue,
    type,
    partyType,
    searchHandlers,
}: {
    control: Control<ContactForm>;
    setValue: UseFormReturn<ContactForm>['setValue'];
    type: 'add' | 'edit';
    partyType: PartyType | undefined;
    searchHandlers: ReturnType<typeof useFuzzySearch>['searchHandlers'];
}) => {
    const [firstName, lastName, watchedName, officialName] = useWatch({
        control,
        name: ['firstName', 'lastName', 'name', 'officialName'],
    });

    // The add form hides a person's unofficial name, so derive it from first/last name there. The edit
    // form shows it as its own field, and deriving on mount would overwrite the stored value.
    // TODO: no-prop-callback-in-effect — setValue is the RHF API; effect-driven sync of derived form fields is the standard pattern here
    useEffect(() => {
        if (type === 'add' && partyType === PartyType.PERSON && (firstName || lastName)) {
            const newName = `${firstName || ''} ${lastName || ''}`.trim();
            if (newName) {
                setValue('name', newName);
            }
        }
    }, [firstName, lastName, partyType, setValue, type]);

    // Trigger fuzzy search when name field changes (only for PERSON type)
    useEffect(() => {
        if (
            type === 'add' &&
            partyType === PartyType.PERSON &&
            searchHandlers?.name &&
            watchedName &&
            watchedName.trim().length > 2
        ) {
            const syntheticEvent = {
                target: {
                    value: watchedName,
                },
            } as React.ChangeEvent<HTMLInputElement>;
            searchHandlers.name.handler(syntheticEvent);
        }
    }, [watchedName, searchHandlers, type, partyType]);

    // Trigger fuzzy search when officialName field changes (only for COMPANY type)
    useEffect(() => {
        if (
            type === 'add' &&
            partyType === PartyType.COMPANY &&
            searchHandlers?.name &&
            officialName &&
            officialName.trim().length > 2
        ) {
            const syntheticEvent = {
                target: {
                    value: officialName,
                },
            } as React.ChangeEvent<HTMLInputElement>;
            searchHandlers.name.handler(syntheticEvent);
        }
    }, [officialName, searchHandlers, type, partyType]);

    return null;
};

const ContactPageForm = (props: ContactFormProps): JSX.Element => {
    const { type = 'add', initialData, contact, refetch } = props;
    useFocusedEditMode('contacts');
    const form = useContactForm(props);
    const { methods, contactId, partyType, searchHandlers, mapCustomFieldsToDto, isEinvoicingEnabled } = form;
    useCreateDialogForm(methods);
    const handleSubmit = useRejectedSubmitCapture(methods.handleSubmit, type === 'edit' ? 'contact-edit' : 'contact');
    const draft = useCreateDraft({
        record: 'contact',
        methods: { handleSubmit, formState: methods.formState },
        toPayload: (formData: ContactForm) =>
            contactFormToRequest(formData, {
                partyType: formData.partyType,
                mapCustomFields: mapCustomFieldsToDto,
                existingBlocks: [],
                isEinvoicingEnabled,
            }),
    });
    const { openModal, closeModal } = useModal();

    const {
        control,
        setValue,
        reset,
        formState: { errors },
    } = methods;

    const sectionErrors = useSectionErrors(
        errors,
        partyType === PartyType.PERSON ? PERSON_FIELD_TO_SECTION_MAP : FIELD_TO_SECTION_MAP
    );

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const returnTo = useReturnToLocation(contactId ? `/contact/${contactId}` : '/contacts');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();
    const leaveDeleted = useLeaveDeletedEntity('/contacts');

    const posthog = usePostHog();
    const [addContact] = useAddContactMutation();
    const [updateContact] = useUpdateContactMutation();
    const [deleteContact] = useDeleteContactMutation();

    // Name syncing and fuzzy search effects are isolated in NameFieldSyncer
    // to avoid re-rendering the entire form on every keystroke in name fields.

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: ContactForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = contactFormToRequest(formData, {
                partyType,
                mapCustomFields: mapCustomFieldsToDto,
                existingBlocks: contact?.blocks ?? [],
                isEinvoicingEnabled,
                initialForm: action === 'update' ? initialData : undefined,
            });

            if (action === 'create') {
                // A draft is created by completing it: the contact keeps the draft's id.
                const created: Promise<{ id: number }> =
                    draft.draftId === null ? addContact({ ...data }).unwrap() : draft.complete(data);
                await created
                    .then((resp) => {
                        posthog.capture('contact_created', {
                            relationship_type: formData.relationshipType,
                            party_type: partyType === 'PERSON' ? 'person' : 'company',
                        });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/contact/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && contactId && !isNaN(Number(contactId))) {
                // Contact blocking change-reasons now flow through the per-row `changeReason` carried
                // in the blocks payload (see buildBlocksPayload); there are no entity-level tracked
                // contact fields left, so entityFieldChangeReasons is always null here.
                await updateContact({
                    ...data,
                    id: Number(contactId),
                    entityFieldChangeReasons: null,
                })
                    .unwrap()
                    .then(() => {
                        if (refetch) refetch();
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(returnTo);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreateContact = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdateContact = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const handleDeleteContact = async () => {
        try {
            if (contactId && !isNaN(Number(contactId))) {
                openModal(
                    <ConfirmationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            void deleteContact({ id: contactId })
                                .unwrap()
                                .then(() => {
                                    bypassNavigationBlockerRef.current = true;
                                    reset({}, { keepValues: true });
                                    leaveDeleted();
                                })
                                .catch(console.error)
                                .finally(() => closeModal());
                        }}
                        content={<p className="text-body1">{t('contacts.delete-confirmation-text')}</p>}
                    />
                );
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }

    if (type === 'add' && !form.hasAnyCreatePermission) {
        return (
            <h6 className="text-h6 p-6 text-center">
                <FormattedMessage id="contacts.no-create-permission" />
            </h6>
        );
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData
                    ? partyNameString(initialData, { useOfficialName: true, lastNameFirst: form.lastNameFirst })
                    : undefined;
            default:
                return t('contacts.add-new-contact');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            if (draft.button) {
                actions.push(draft.button);
            }
            actions.push(
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS']}
                    onClick={submitCreateContact}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && contactId) {
            actions.push(
                <DeleteButton
                    key="editDelete"
                    hasAuthority={contact?.isDeletable}
                    onClick={() => {
                        void handleDeleteContact();
                    }}
                    disabled={buttonsDisabled}
                />,
                <SaveButton
                    key="editUpdate"
                    hasAuthority={contact?.isEditable}
                    onClick={submitUpdateContact}
                    disabled={buttonsDisabled}
                />
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        <NameFieldSyncer
                            control={control}
                            setValue={setValue}
                            type={type}
                            partyType={partyType}
                            searchHandlers={searchHandlers}
                        />
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section
                                    title="contacts.choose-type"
                                    isHidden={type === 'edit' && !form.isContactTypeControlEnabled}
                                >
                                    <ChooseTypeFields form={form} />
                                </Section>

                                <Section title="contacts.about.title" collapsible>
                                    <AboutFields form={form} />
                                </Section>

                                <Section
                                    title="contacts.person.title"
                                    isHidden={partyType !== PartyType.PERSON}
                                    collapsible
                                >
                                    <PersonFields form={form} />
                                </Section>

                                <Section
                                    title="contacts.company.title"
                                    isHidden={partyType !== PartyType.COMPANY}
                                    collapsible
                                >
                                    <CompanyFields form={form} />
                                </Section>

                                <Section
                                    title="contacts.identity-document.title"
                                    isHidden={partyType !== PartyType.PERSON || !form.isContactDocumentFieldsEnabled}
                                    collapsible
                                >
                                    <IdentityDocumentFields form={form} />
                                </Section>

                                <Section title="contacts.contact-details" collapsible>
                                    <ContactDetailsFields form={form} />
                                </Section>

                                <Section title="contacts.billing.title" collapsible>
                                    <BillingFields form={form} />
                                </Section>

                                <Section
                                    title="contacts.marketing.title"
                                    isHidden={partyType !== PartyType.PERSON}
                                    collapsible
                                >
                                    <MarketingFields form={form} />
                                </Section>

                                <Section title="contacts.other.title" collapsible>
                                    <OtherFields form={form} />
                                </Section>

                                <Section title="contacts.blocking.policy-title" collapsible>
                                    <BlockingPolicyEditor contact={contact} />
                                </Section>

                                <Section title="contacts.compliance.title" collapsible>
                                    <ComplianceFields form={form} />
                                </Section>

                                <CustomFields
                                    control={control as unknown as Control<FieldValues>}
                                    fields={form.customFields}
                                />
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
            <ConfirmNavigation
                control={control}
                bypassRef={bypassNavigationBlockerRef}
                draft={type === 'add' ? draft.leave : null}
            />
        </FormProvider>
    );
};

export default ContactPageForm;

const invoiceDefaultsToDto = (form: ContactForm) => ({
    invoiceTemplateId: form.invoiceTemplate?.id ?? null,
    invoiceSpecTemplateId: form.invoiceSpecTemplate?.id ?? null,
    isSpecIncludedInInvoice: form.isSpecIncludedInInvoice?.value ?? null,
    invoiceCustomization: form.invoiceCustomization,
});

// Map form to person creation dto
export const mapToPersonDto = (
    form: ContactForm,
    customFields: CustomFieldDto[] = [],
    existingBlocks: PartyBlockDto[] = []
): ContactDataCreateOrUpdatePersonDto => ({
    partyType: form?.partyType === 'PERSON' ? PartyType.PERSON : PartyType.COMPANY,
    relationshipTypeCode:
        form.relationshipType === 'CLIENT'
            ? RelationshipTypeCode.CLIENT
            : form.relationshipType === 'LEAD'
              ? RelationshipTypeCode.LEAD
              : RelationshipTypeCode.NON_CLIENT,
    name: form.name,
    mainEmail: form.mainEmail || null,
    mainPhone: form.mainPhone || null,
    mainMobile: form.mainMobile || null,
    statusCode: form.statusCode,
    clientReference: form.clientReference,
    preferredLanguageCode: form.preferredLanguage ? form.preferredLanguage.value : null,
    billingNotes: form.billingNotes || null,
    invoicingInfo: form.invoicingInfo || null,
    payerParty: form.payerParty ?? null,
    useJointInvoicing: form.useJointInvoicing,
    issueInvoicesWithCurrentDate: form.issueInvoicesWithCurrentDate,
    isInternalClient: form.isInternalClient,
    primaryRecipientParty: form.primaryRecipientParty,
    primaryRecipientAdditionalEmails: form.primaryRecipientAdditionalEmails || null,
    secondaryRecipientParties: form.secondaryRecipientParties.map(({ id, name }) => ({ id, name })),
    secondaryRecipientAdditionalEmails: form.secondaryRecipientAdditionalEmails || null,
    invoiceArchiveEmail: form.invoiceArchiveEmail || null,
    ...invoiceDefaultsToDto(form),
    debtNotes: form.debtNotes || null,
    debtExplanation: form.debtExplanation || null,
    debtHistory: form.debtHistory || null,
    notes: form.notes || null,
    coiNotes: form.coiNotes || null,
    relations: [],
    relatesTo: getRelationsTo(form.employedBy, form.primaryContactForCompanies),
    segmentTypeCode: form.segmentType ? form.segmentType.value : null,
    serviceLevelTypeCode: form.serviceLevelType ? form.serviceLevelType.value : null,
    businessAreas: form.businessAreas ? form.businessAreas.map((val) => ({ businessAreaCode: val.value })) : [],
    relatedKeywords: form.relatedKeywords
        ? form.relatedKeywords.map((val) => ({ keywordTypeCode: val.value, notes: val.notes }))
        : [],
    businessContacts: form.businessContacts
        ? form.businessContacts.map((val) => ({
              employee: { id: val.id },
              partyEmployeeTypeCode: 'BUSINESS_CONTACT_OF',
          }))
        : [],
    managers: form.managers
        ? form.managers.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'RELATIONSHIP_MANAGER' }))
        : [],
    partners: form.partners
        ? form.partners.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'RELATIONSHIP_PARTNER' }))
        : [],
    administrators: form.administrators
        ? form.administrators.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'PARTY_ADMINISTRATOR' }))
        : [],
    paymentNotificationReceivers: form.paymentNotificationReceivers
        ? form.paymentNotificationReceivers.map((val) => ({
              employee: { id: val.id },
              partyEmployeeTypeCode: 'PAYMENT_NOTIFICATION_RECEIVER',
          }))
        : [],
    personalCode: form.personalCode || null,
    personTitleCode: form.personTitle ? form.personTitle.value : null,
    personTitleText: form.personManualTitle ? form.personManualTitle : null,
    linkedinUrl: form.linkedinUrl || null,
    position: form.position || null,
    billingAddress: form.billingAddress,
    billingLocalAddress: form.billingLocalAddress,
    homeAddress: form.homeAddress,
    homeLocalAddress: form.homeLocalAddress,
    correspondenceAddress: form.correspondenceAddress,
    firstName: form.firstName,
    lastName: form.lastName,
    localFirstName: form.localFirstName || null,
    localLastName: form.localLastName || null,
    contacts: null,
    paymentTermTypeCode: null,
    invoicingIntervalTypeCode: null,
    invoicingPaymentDays:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.invoicingPaymentDays ?? null) : null,
    preferredBankAccountId:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.preferredBankAccount?.value ?? null) : null,
    dmsLink: form.dmsLink || null,
    isPoliticallyExposedPerson: form.isPoliticallyExposedPerson,
    identityDocumentNumber: form.identityDocumentNumber || null,
    identityDocumentDateOfIssue: form.identityDocumentDateOfIssue || null,
    identityDocumentDateOfExpiry: form.identityDocumentDateOfExpiry || null,
    identityDocumentIssuingCountryCode: form.identityDocumentIssuingCountryCode?.value || null,
    identityDocumentIssuingAuthority: form.identityDocumentIssuingAuthority || null,
    invoicingDeadlineDay:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.invoicingDeadlineDay?.id ?? null) : null,
    externalBillingInfo: form.relationshipType == RelationshipTypeCode.CLIENT ? form.externalBillingInfo : null,
    isPersonaNonGrata: form.isPersonaNonGrata,
    riskProfileTypeCode: form.riskProfileTypeCode ? form.riskProfileTypeCode.value : null,
    riskProfileNotes: form.riskProfileNotes || null,
    complianceVerificationStatusTypeCode: form.complianceVerificationStatusTypeCode
        ? form.complianceVerificationStatusTypeCode.value
        : null,
    complianceVerificationNotes: form.complianceVerificationNotes || null,
    preferredMailingLanguageTypeCode: form.preferredMailingLanguageTypeCode
        ? form.preferredMailingLanguageTypeCode.value
        : null,
    countries: form.countries ? form.countries.map((val) => ({ countryTypeCode: val.value })) : [],
    mailingPreferences: form.mailingPreferences
        ? form.mailingPreferences.map((val) => ({ mailingPreferenceTypeCode: val.value }))
        : [],
    marketingAreaTypes: form.marketingAreaTypes
        ? form.marketingAreaTypes.map((val) => ({ marketingAreaTypeCode: val.value }))
        : [],
    areaOfResponsibilityTypes: form.areaOfResponsibilityTypes
        ? form.areaOfResponsibilityTypes.map((val) => ({ areaOfResponsibilityTypeCode: val.value }))
        : [],
    cooperationAreaTypes: form.cooperationAreaTypes
        ? form.cooperationAreaTypes.map((val) => ({ cooperationAreaTypeCode: val.value }))
        : [],
    isServiceAgreementNotNeeded: form.isServiceAgreementNotNeeded,
    isUboNotNeeded: form.isUboNotNeeded,
    entityFieldChangeReasons: null,
    riskFactors: form.riskFactors?.map((rf) => {
        return { riskFactorTypeCode: rf.value };
    }),
    partyGroups: form.partyGroups.map(({ id }) => ({ id })),
    blocks: buildBlocksPayload(form.blocking, existingBlocks),
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
});

// Map form to company creation dto
export const mapToCompanyDto = (
    form: ContactForm,
    customFields: CustomFieldDto[] = [],
    existingBlocks: PartyBlockDto[] = []
): ContactDataCreateOrUpdateCompanyDto => ({
    partyType: form.partyType === 'PERSON' ? PartyType.PERSON : PartyType.COMPANY,
    relationshipTypeCode:
        form.relationshipType === 'CLIENT'
            ? RelationshipTypeCode.CLIENT
            : form.relationshipType === 'LEAD'
              ? RelationshipTypeCode.LEAD
              : RelationshipTypeCode.NON_CLIENT,
    name: form.name,
    officialLocalName: form.officialLocalName || null,
    mainEmail: form.mainEmail || null,
    mainPhone: form.mainPhone || null,
    mainMobile: form.mainMobile || null,
    statusCode: form.statusCode,
    clientReference: form.clientReference,
    preferredLanguageCode: form.preferredLanguage ? form.preferredLanguage.value : null,
    billingNotes: form.billingNotes || null,
    invoicingInfo: form.invoicingInfo || null,
    payerParty: form.payerParty ?? null,
    useJointInvoicing: form.useJointInvoicing,
    issueInvoicesWithCurrentDate: form.issueInvoicesWithCurrentDate,
    isInternalClient: form.isInternalClient,
    primaryRecipientParty: form.primaryRecipientParty,
    primaryRecipientAdditionalEmails: form.primaryRecipientAdditionalEmails || null,
    secondaryRecipientParties: form.secondaryRecipientParties.map(({ id }) => ({ id })),
    secondaryRecipientAdditionalEmails: form.secondaryRecipientAdditionalEmails || null,
    invoiceArchiveEmail: form.invoiceArchiveEmail || null,
    ...invoiceDefaultsToDto(form),
    debtNotes: form.debtNotes || null,
    debtExplanation: form.debtExplanation || null,
    debtHistory: form.debtHistory || null,
    notes: form.notes || null,
    coiNotes: form.coiNotes || null,
    relations: getRelations(form.employees, form.primaryContact),
    relatesTo: [],
    businessAreas: form.businessAreas ? form.businessAreas.map((val) => ({ businessAreaCode: val.value })) : [],
    relatedKeywords: form.relatedKeywords
        ? form.relatedKeywords.map((val) => ({ keywordTypeCode: val.value, notes: val.notes }))
        : [],
    businessContacts: form.businessContacts
        ? form.businessContacts.map((val) => ({
              employee: { id: val.id },
              partyEmployeeTypeCode: 'BUSINESS_CONTACT_OF',
          }))
        : [],
    managers: form.managers
        ? form.managers.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'RELATIONSHIP_MANAGER' }))
        : [],
    partners: form.partners
        ? form.partners.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'RELATIONSHIP_PARTNER' }))
        : [],
    administrators: form.administrators
        ? form.administrators.map((val) => ({ employee: { id: val.id }, partyEmployeeTypeCode: 'PARTY_ADMINISTRATOR' }))
        : [],
    paymentNotificationReceivers: form.paymentNotificationReceivers
        ? form.paymentNotificationReceivers.map((val) => ({
              employee: { id: val.id },
              partyEmployeeTypeCode: 'PAYMENT_NOTIFICATION_RECEIVER',
          }))
        : [],
    officialName: form.officialName,
    regCode: form.regCode || null,
    isVatLiable: !form.notVatLiable,
    vatRegNum: form.notVatLiable ? null : form.vatRegNum || null,
    vatCountryCode: form.notVatLiable ? null : (form.vatCountryCode?.value ?? null),
    website: form.website || null,
    einvoiceAddress: form.einvoiceAddress || null,
    einvoiceOperatorCode: form.einvoiceOperatorType?.value === 'PEPPOL' ? null : form.einvoiceOperatorCode || null,
    einvoiceOperatorType: (form.einvoiceOperatorType?.value as EinvoiceOperatorType) ?? null,
    billingAddress: form.billingAddress,
    billingLocalAddress: form.billingLocalAddress,
    visitingAddress: form.visitingAddress,
    correspondenceAddress: form.correspondenceAddress,
    segmentTypeCode: form.segmentType ? form.segmentType.value : null,
    serviceLevelTypeCode: form.serviceLevelType ? form.serviceLevelType.value : null,
    registrationAddress: form.registrationAddress,
    registrationLocalAddress: form.registrationLocalAddress,
    contacts: null,
    paymentTermTypeCode: null,
    invoicingIntervalTypeCode: null,
    invoicingPaymentDays:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.invoicingPaymentDays ?? null) : null,
    preferredBankAccountId:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.preferredBankAccount?.value ?? null) : null,
    dmsLink: form.dmsLink || null,
    invoicingDeadlineDay:
        form.relationshipType == RelationshipTypeCode.CLIENT ? (form.invoicingDeadlineDay?.id ?? null) : null,
    externalBillingInfo: form.relationshipType == RelationshipTypeCode.CLIENT ? form.externalBillingInfo : null,
    isPersonaNonGrata: form.isPersonaNonGrata,
    riskProfileTypeCode: form.riskProfileTypeCode ? form.riskProfileTypeCode.value : null,
    riskProfileNotes: form.riskProfileNotes || null,
    complianceVerificationStatusTypeCode: form.complianceVerificationStatusTypeCode
        ? form.complianceVerificationStatusTypeCode.value
        : null,
    complianceVerificationNotes: form.complianceVerificationNotes || null,
    preferredMailingLanguageTypeCode: form.preferredMailingLanguageTypeCode
        ? form.preferredMailingLanguageTypeCode.value
        : null,
    countries: form.countries ? form.countries.map((val) => ({ countryTypeCode: val.value })) : [],
    mailingPreferences: form.mailingPreferences
        ? form.mailingPreferences.map((val) => ({ mailingPreferenceTypeCode: val.value }))
        : [],
    marketingAreaTypes: form.marketingAreaTypes
        ? form.marketingAreaTypes.map((val) => ({ marketingAreaTypeCode: val.value }))
        : [],
    cooperationAreaTypes: form.cooperationAreaTypes
        ? form.cooperationAreaTypes.map((val) => ({ cooperationAreaTypeCode: val.value }))
        : [],
    isServiceAgreementNotNeeded: form.isServiceAgreementNotNeeded,
    isUboNotNeeded: form.isUboNotNeeded,
    entityFieldChangeReasons: null,
    riskFactors: form.riskFactors?.map((rf) => {
        return { riskFactorTypeCode: rf.value };
    }),
    partyGroups: form.partyGroups.map(({ id }) => ({ id })),
    blocks: buildBlocksPayload(form.blocking, existingBlocks),
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
});

const getRelationsTo = (
    employeedBy: { name: string; id: number }[] | null | undefined,
    primaryContactForCompanies: { name: string; id: number }[] | null | undefined
) => {
    const relations =
        employeedBy?.map((val) => ({
            partyRelationshipTypeCode: PartyRelationshipTypeCode.EMPLOYEE,
            otherParty: { id: val.id, name: val.name },
        })) || [];

    const companies =
        primaryContactForCompanies?.map((primaryContact) => ({
            partyRelationshipTypeCode: PartyRelationshipTypeCode.PRIMARY_CONTACT,
            otherParty: { id: primaryContact.id, name: primaryContact.name },
        })) || [];

    if (companies.length > 0) relations.push(...companies);

    return relations;
};

const getRelations = (
    employees: { name: string; id: number }[] | null | undefined,
    primaryContact?: { name: string; id: number } | null | undefined
) => {
    const relations =
        employees?.map((val) => ({
            partyRelationshipTypeCode: PartyRelationshipTypeCode.EMPLOYEE,
            otherParty: { id: val.id, name: val.name },
        })) || [];

    if (primaryContact) {
        relations.push({
            partyRelationshipTypeCode: PartyRelationshipTypeCode.PRIMARY_CONTACT,
            otherParty: { id: primaryContact.id, name: primaryContact.name },
        });
    }

    return relations;
};
